import type { GridAudioSoundPlayer } from "../GridLayer";
import type { ColumnLanding, GridLandReaction } from "./GridLandReaction";
import { landSoundIds } from "./SymbolLandSounds";

const SEMITONE_RATIO = Math.pow(2, 1 / 12);

/**
 * One land sound played with a RISING pitch per occurrence across a whole spin (the classic scatter
 * arpeggio): the Nth landing of `soundId` plays `semitoneStep × N` semitones up. Landings inside one
 * column are staggered; a slam-stop burst (several columns landing within `burstSuppressMs`) plays
 * once. Silent on tumble drops — the stinger belongs to the spin reveal. The counter resets on
 * `onSequenceStart`.
 */
export class PitchedLandSound implements GridLandReaction {
  private readonly audio: GridAudioSoundPlayer;
  private readonly soundId: string;
  private readonly semitoneStep: number;
  private readonly staggerSeconds: number;
  private readonly burstSuppressMs: number;
  private readonly nowMs: () => number;
  private count = 0;
  private lastPlayMs = Number.NEGATIVE_INFINITY;

  public constructor(input: {
    audio: GridAudioSoundPlayer;
    soundId: string;
    semitoneStep?: number;
    staggerSeconds?: number;
    burstSuppressMs?: number;
    /**
     * Engine time in MILLISECONDS, compared directly with `burstSuppressMs`. The runner's
     * `engineSeconds` is in seconds, so a game passes `() => runner.engineSeconds * 1000`. Engine
     * time, so the burst check pauses and frame-steps with the game; until 2026-09-26 it defaulted to
     * the wall clock, so landings frame-stepped apart under the dev pause counted as separate bursts
     * (review finding 55).
     */
    nowMs: () => number;
  }) {
    this.audio = input.audio;
    this.soundId = input.soundId;
    this.semitoneStep = input.semitoneStep ?? 1;
    this.staggerSeconds = input.staggerSeconds ?? 0.08;
    this.burstSuppressMs = input.burstSuppressMs ?? 50;
    this.nowMs = input.nowMs;
  }

  public onSequenceStart(): void {
    this.count = 0;
    this.lastPlayMs = Number.NEGATIVE_INFINITY;
  }

  public onColumnLanded(landing: ColumnLanding): void {
    if (landing.isChainDrop) return;
    const occurrences = landSoundIds(landing).filter((id) => id === this.soundId).length;
    const nowMs = this.nowMs();
    for (let i = 0; i < occurrences; i++) {
      const delaySeconds = i * this.staggerSeconds;
      const expectedPlayMs = nowMs + delaySeconds * 1000;
      if (expectedPlayMs < this.lastPlayMs + this.burstSuppressMs) continue;
      const playbackRate = Math.pow(SEMITONE_RATIO, this.count * this.semitoneStep);
      this.count += 1;
      this.lastPlayMs = expectedPlayMs;
      if (delaySeconds <= 0 || !this.audio.playSoundIdDelayed) this.audio.playSoundId(this.soundId, { playbackRate });
      else this.audio.playSoundIdDelayed(this.soundId, delaySeconds, { playbackRate });
    }
  }
}
